import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import LoadingFallback from '../components/LoadingFallback';
import StatusChip from '../components/StatusChip';
import { api, apiErrorMessage } from '../api/client';
import { useFeedback } from '../contexts/FeedbackContext';
import type { Meet, Venue } from '../api/types';
import dayjs from 'dayjs';

export default function MeetsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [open, setOpen] = useState(false);

  const meetsQuery = useQuery<Meet[]>({
    queryKey: ['meets'],
    queryFn: async () => (await api.get('/meets')).data,
  });

  const venuesQuery = useQuery<Venue[]>({
    queryKey: ['venues'],
    queryFn: async () => (await api.get('/venues')).data,
  });

  const createMutation = useMutation({
    mutationFn: async (payload: any) => (await api.post('/meets', payload)).data,
    onSuccess: (meet: Meet) => {
      toast({ message: 'Meet created. Add events and entries next.', severity: 'success' });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['meets'] });
      navigate(`/meets/${meet.id}/prepare`);
    },
    onError: (e) => toast({ message: apiErrorMessage(e), severity: 'error' }),
  });

  return (
    <Box>
      <PageHeader
        title="Meets"
        subtitle="Plan competitions, register athletes, and email parents."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            aria-label="Create meet"
          >
            Create meet
          </Button>
        }
      />

      <Card>
        {meetsQuery.isLoading ? (
          <LoadingFallback />
        ) : (meetsQuery.data ?? []).length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptyState
              title="No meets yet"
              description="Create a meet to add events and start registering athletes."
              action={
                <Button variant="contained" onClick={() => setOpen(true)} aria-label="Create first meet">
                  Create meet
                </Button>
              }
            />
          </Box>
        ) : (
          <Table size="small" aria-label="Meets">
            <TableHead>
              <TableRow>
                <TableCell>Meet</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Venue</TableCell>
                <TableCell>Events</TableCell>
                <TableCell>Entries</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(meetsQuery.data ?? []).map((m) => (
                <TableRow
                  key={m.id}
                  hover
                  component={RouterLink}
                  to={`/meets/${m.id}`}
                  sx={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                  aria-label={`Open ${m.name}`}
                >
                  <TableCell>
                    <Typography variant="subtitle2">{m.name}</Typography>
                    {m.description && (
                      <Typography variant="caption" color="text.secondary">{m.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {dayjs(m.start_date).format('MMM D, YYYY')}
                    {m.end_date !== m.start_date && ` – ${dayjs(m.end_date).format('MMM D')}`}
                  </TableCell>
                  <TableCell>{m.venue?.name || '—'}</TableCell>
                  <TableCell>{m.event_count ?? 0}</TableCell>
                  <TableCell>{m.registration_count ?? 0}</TableCell>
                  <TableCell><StatusChip status={m.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <CreateMeetDialog
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={(p) => createMutation.mutate(p)}
        venues={venuesQuery.data ?? []}
        busy={createMutation.isPending}
      />
    </Box>
  );
}

function CreateMeetDialog({
  open,
  onClose,
  onSubmit,
  venues,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (p: any) => void;
  venues: Venue[];
  busy: boolean;
}) {
  const [name, setName] = useState('');
  const [start, setStart] = useState(dayjs().format('YYYY-MM-DD'));
  const [end, setEnd] = useState(dayjs().format('YYYY-MM-DD'));
  const [venueId, setVenueId] = useState<string>('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName('');
      setStart(dayjs().format('YYYY-MM-DD'));
      setEnd(dayjs().format('YYYY-MM-DD'));
      setVenueId('');
      setDescription('');
      setError(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create meet</DialogTitle>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (dayjs(end).isBefore(dayjs(start))) {
            setError('End date must be on or after start date.');
            return;
          }
          setError(null);
          onSubmit({
            name,
            start_date: start,
            end_date: end,
            venue_id: venueId ? Number(venueId) : undefined,
            description: description || undefined,
          });
        }}
        aria-label="Create meet form"
      >
        <DialogContent>
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Meet name" value={name} onChange={(e) => setName(e.target.value)} required />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start date"
                  type="date"
                  value={start}
                  onChange={(e) => setStart(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="End date"
                  type="date"
                  value={end}
                  onChange={(e) => setEnd(e.target.value)}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Venue"
              select
              value={venueId}
              onChange={(e) => setVenueId(e.target.value)}
            >
              <MenuItem value="">— None —</MenuItem>
              {venues.map((v) => (
                <MenuItem key={v.id} value={String(v.id)}>{v.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} aria-label="Cancel">Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy} aria-label="Create meet">
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
